import React, { useEffect, useState } from "react";
import { TbBrandTwilio } from "react-icons/tb";
import { useProperty } from "../../../../Contexts/PropertyContext/PropertyContext";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { apiClient } from "../../../../api";
import { baseUrl } from "../../../../Environments/environment";

const TwilioSetup = () => {
	const { selectedPropertyId } = useProperty();

	const [propertyName, setPropertyName] = useState("");

	const [initialLoading, setInitialLoading] = useState(false);

	const [billingLoading, setBillingLoading] = useState(false);

	const [countryCode, setCountryCode] = useState("");

	const [phoneNumber, setPhoneNumber] = useState("");

	const [twilioId, setTwilioId] = useState("");

	const [status, setStatus] = useState("active");

	const [billing, setBilling] = useState(null);
	const [billingAnalytics, setBillingAnalytics] = useState(null);

	const currentDate = new Date();

	const currentMonth = currentDate.getMonth() + 1;

	const currentYear = currentDate.getFullYear();

	const [selectedMonth, setSelectedMonth] = useState(currentMonth);

	const [selectedYear, setSelectedYear] = useState(currentYear);

	const navigate = useNavigate();

	// Generate years from 2025 → current year
	const years = [];

	for (let year = 2025; year <= currentYear; year++) {
		years.push(year);
	}

	const months = [
		{
			value: 1,
			label: "January",
		},
		{
			value: 2,
			label: "February",
		},
		{
			value: 3,
			label: "March",
		},
		{
			value: 4,
			label: "April",
		},
		{
			value: 5,
			label: "May",
		},
		{
			value: 6,
			label: "June",
		},
		{
			value: 7,
			label: "July",
		},
		{
			value: 8,
			label: "August",
		},
		{
			value: 9,
			label: "September",
		},
		{
			value: 10,
			label: "October",
		},
		{
			value: 11,
			label: "November",
		},
		{
			value: 12,
			label: "December",
		},
	];

	// Disable future months
	const availableMonths =
		selectedYear === currentYear
			? months.filter((month) => month.value <= currentMonth)
			: months;

	useEffect(() => {
		if (!selectedPropertyId) {
			navigate("/propertyhome");

			toast.warn("Select the property first!!");
		}
	}, [selectedPropertyId, navigate]);

	// Load static twilio/property info once
	useEffect(() => {
		if (selectedPropertyId) {
			fetchInitialData(selectedPropertyId);
		}
	}, [selectedPropertyId]);

	// Load only billing when month/year changes
	useEffect(() => {
		if (selectedPropertyId) {
			fetchBillingData(selectedPropertyId);
		}
	}, [selectedMonth, selectedYear]);

	const fetchInitialData = async (propertyId) => {
		try {
			setInitialLoading(true);

			// Property API
			const response = await apiClient.get(`${baseUrl}/property/${propertyId}`);

			// STATIC TWILIO API
			const twilloAccount = await apiClient.get(
				`${baseUrl}/twilioAccount/${propertyId}`,
			);

			if (response.status === 200) {
				const { property } = response.data.result.property;

				setPropertyName(property.name);
			}

			if (twilloAccount.status === 200) {
				const result = twilloAccount.data.result;

				setCountryCode(result.countryCode);

				setPhoneNumber(result.phoneNumber);

				setTwilioId(result.twilioId);
				setBillingAnalytics(result.billing);

				if (result.status) {
					setStatus(result.status);
				}
			}
		} catch (error) {
			console.error(error);

			const errorMessage =
				error.response?.data?.message || "Error fetching property data.";

			toast.error(errorMessage);
		} finally {
			setInitialLoading(false);
		}
	};

	const handleAddProperty = () => {
		navigate("/addproperty", {
			state: {
				from: "twilio-setup",
				isComplete: false,
				propertyId: selectedPropertyId,
				isSubaccountCreated: true,
			},
		});
	};

	const fetchBillingData = async (propertyId) => {
		try {
			setBillingLoading(true);

			// MONTHLY BILLING API
			const billingResponse = await apiClient.get(
				`${baseUrl}/twilioAccount/${propertyId}/monthly?month=${selectedMonth}&year=${selectedYear}`,
			);

			if (billingResponse.status === 200) {
				const result = billingResponse.data.result;

				setBilling(result.billing);
			}
		} catch (error) {
			console.error(error);

			toast.error("Failed to fetch billing data.");
		} finally {
			setBillingLoading(false);
		}
	};

	const SkeletonRow = () => (
		<div className="animate-pulse bg-gray-200 rounded h-4 w-full"></div>
	);

	return (
		<div className="bg-gray-100 p-6 flex justify-center overflow-hidden">
			<div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6 flex items-start w-full max-w-6xl overflow-x-auto">
				{/* Icon Section */}
				<div className="flex-shrink-0 mr-8 -mt-2">
					<TbBrandTwilio className="text-indigo-600 w-24 h-24" />
				</div>

				{/* Content Section */}
				<div className="w-full">
					{/* Header */}
					<div className="mb-8">
						{initialLoading ? (
							<>
								<div className="animate-pulse bg-gray-200 rounded h-8 w-64 mb-3"></div>

								<div className="animate-pulse bg-gray-200 rounded h-4 w-96"></div>
							</>
						) : (
							<>
								<h2 className="text-2xl font-semibold text-gray-800 mb-2">
									Twilio Setup
								</h2>

								<p className="text-gray-600 text-sm max-w-xl">
									Monitor your Twilio messaging activity, billing analytics, and
									phone number status for this property.
								</p>
							</>
						)}
					</div>

					{/* Twilio Details */}
					<div className="grid grid-cols-2 gap-4 mb-8 text-gray-800">
						{initialLoading ? (
							<>
								{Array.from({
									length: 6,
								}).map((_, idx) => (
									<div key={idx}>
										<div className="animate-pulse bg-gray-200 rounded h-4 w-32 mb-2"></div>

										<div className="animate-pulse bg-gray-200 rounded h-5 w-48"></div>
									</div>
								))}
							</>
						) : (
							<>
								<div>
									<h3 className="font-semibold">Property Name:</h3>

									<p className="text-gray-600">{propertyName}</p>
								</div>

								<div>
									<h3 className="font-semibold">Twilio ID:</h3>

									<p className="text-gray-600 break-all">{twilioId}</p>
								</div>

								<div>
									<h3 className="font-semibold">Account Status:</h3>

									<p
										className={`font-medium ${
											status === "active" ? "text-green-600" : "text-red-600"
										}`}
									>
										{status
											? status.charAt(0).toUpperCase() + status.slice(1)
											: "Inactive"}
									</p>
								</div>

								<div>
									<h3 className="font-semibold">Phone Number:</h3>

									<p className="text-gray-600">
										{`${countryCode} ${phoneNumber}`}
									</p>
								</div>

								<div>
									<h3 className="font-semibold">Messages Delivered:</h3>

									<p className="text-gray-600">
										{billingAnalytics?.delivered || 0}
									</p>
								</div>

								<div>
									<h3 className="font-semibold">Delivery Rate:</h3>

									<p className="text-green-600 font-medium">
										{billingAnalytics?.deliveryRate || 0}%
									</p>
								</div>
							</>
						)}
					</div>

					{/* Billing Section */}
					<div className="mt-4">
						<div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
							<h3 className="text-lg font-semibold text-gray-800">
								Monthly Billing Overview
							</h3>

							{/* Filters */}
							<div className="flex flex-col sm:flex-row gap-4">
								{/* Month Select */}
								<div className="min-w-[170px]">
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Month
									</label>

									<select
										value={selectedMonth}
										onChange={(e) => setSelectedMonth(Number(e.target.value))}
										className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
									>
										{availableMonths.map((month) => (
											<option key={month.value} value={month.value}>
												{month.label}
											</option>
										))}
									</select>
								</div>

								{/* Year Select */}
								<div className="min-w-[140px]">
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Year
									</label>

									<select
										value={selectedYear}
										onChange={(e) => {
											const newYear = Number(e.target.value);

											setSelectedYear(newYear);

											if (
												newYear === currentYear &&
												selectedMonth > currentMonth
											) {
												setSelectedMonth(currentMonth);
											}
										}}
										className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
									>
										{years.map((year) => (
											<option key={year} value={year}>
												{year}
											</option>
										))}
									</select>
								</div>
							</div>
						</div>

						{/* Billing Table */}
						<div className="overflow-x-auto border border-gray-200 rounded-lg">
							<table className="min-w-full text-sm text-left text-gray-700">
								<thead className="bg-gray-100 text-gray-700 uppercase text-xs">
									<tr>
										<th className="py-3 px-4">Month</th>

										<th className="py-3 px-4">SMS Used</th>

										<th className="py-3 px-4">Delivered</th>

										<th className="py-3 px-4">Failed</th>

										<th className="py-3 px-4">Number Status</th>

										<th className="py-3 px-4">Number Cost</th>

										<th className="py-3 px-4">SMS Cost</th>

										<th className="py-3 px-4">Total Cost</th>
									</tr>
								</thead>

								<tbody className="bg-white divide-y divide-gray-200">
									{billingLoading ? (
										<tr>
											{Array.from({
												length: 8,
											}).map((_, idx) => (
												<td key={idx} className="py-4 px-4">
													<SkeletonRow />
												</td>
											))}
										</tr>
									) : (
										<tr className="hover:bg-gray-50 transition-all">
											<td className="py-3 px-4 font-medium">
												{billing?.month || "-"}
											</td>

											<td className="py-3 px-4">{billing?.smsSent || 0}</td>

											<td className="py-3 px-4 text-green-600 font-medium">
												{billing?.delivered || 0}
											</td>

											<td className="py-3 px-4 text-red-600 font-medium">
												{billing?.failed || 0}
											</td>

											<td
												className={`py-3 px-4 font-medium ${
													billing?.phoneNumberStatus === "in-use"
														? "text-green-600"
														: "text-red-600"
												}`}
											>
												{billing?.phoneNumberStatus || (
													<button
														onClick={handleAddProperty}
														className="text-indigo-600 hover:text-indigo-800 font-medium transition underline"
													>
														Add Phone Number
													</button>
												)}
											</td>

											<td className="py-3 px-4">
												${billing?.phoneNumberCost?.toFixed(2) || "0.00"}
											</td>

											<td className="py-3 px-4">
												${billing?.smsCost?.toFixed(2) || "0.00"}
											</td>

											<td className="py-3 px-4 text-indigo-600 font-semibold">
												${billing?.totalCost?.toFixed(2) || "0.00"}
											</td>
										</tr>
									)}
								</tbody>
							</table>
						</div>
					</div>

					{/* Additional Stats */}
					<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
						{billingLoading ? (
							Array.from({
								length: 3,
							}).map((_, idx) => (
								<div key={idx} className="bg-gray-50 rounded-lg border p-4">
									<div className="animate-pulse bg-gray-200 rounded h-4 w-24 mb-3"></div>

									<div className="animate-pulse bg-gray-200 rounded h-6 w-16"></div>
								</div>
							))
						) : (
							<>
								<div className="bg-gray-50 rounded-lg border p-4">
									<h3 className="font-semibold text-gray-700 text-sm">
										Delivered
									</h3>

									<p className="text-green-600 text-lg font-semibold mt-1">
										{billing?.delivered || 0}
									</p>
								</div>

								<div className="bg-gray-50 rounded-lg border p-4">
									<h3 className="font-semibold text-gray-700 text-sm">
										Failed
									</h3>

									<p className="text-red-600 text-lg font-semibold mt-1">
										{billing?.failed || 0}
									</p>
								</div>

								<div className="bg-gray-50 rounded-lg border p-4">
									<h3 className="font-semibold text-gray-700 text-sm">
										Current Spend
									</h3>

									<p className="text-indigo-600 text-lg font-semibold mt-1">
										${billing?.totalCost?.toFixed(2) || "0.00"}
									</p>
								</div>
							</>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default TwilioSetup;
